<template>
	<div class="event-card full-width q-pb-md">
		<q-tabs v-model="event_tab" class="full-width">
			<q-tab
				v-for="(event, index) in event_dates"
				:key="`t${index}`"
				slot="title"
				:name="`${index}`"
				:label="fDate(event.hr_start)"
			/>

			<q-tab-pane
				v-for="(event, index) in event_dates"
				:key="`tp${index}`"
				:name="`${index}`"
			>
				<div class="row gutter-xs">
					<div class="col-12">
						<q-select
							v-model="event.event_date_type"
							:options="
								event_date_types.map((e) => ({ label: $t(e.slug), value: e.value }))
							"
							:error="$v.event_date_type.$error"
							@input="$v.event_date_type.$touch"
							:disable="
								event.id !== 0 ||
									disableEvents ||
									!permissions.presential_edit_events
							"
							:float-label="$t('presential_event_type')"
						/>
					</div>

					<div class="col-xs-12 col-sm-6 gutter">
						<q-datetime
							v-model="event.hr_start"
							:float-label="$t('event_start')"
							type="datetime"
							format="DD/MM/YYYY HH:mm"
							clearable
							:error="$v.hr_start.$error"
							@input="$v.hr_start.$touch"
							:disable="disableEvents || !permissions.presential_edit_events"
						/>
					</div>

					<div class="col-xs-12 col-sm-6 gutter">
						<q-datetime
							v-model="event.hr_end"
							:float-label="$t('event_end')"
							type="datetime"
							format="DD/MM/YYYY HH:mm"
							clearable
							:error="$v.hr_end.$error"
							@input="$v.hr_end.$touch"
							:disable="disableEvents || !permissions.presential_edit_events"
						/>
					</div>

					<div class="col-12">
						<q-select
							v-model="event.has_test"
							:options="event_test_types"
							:error="$v.has_test.$error"
							@input="$v.has_test.$touch"
							:float-label="$t('will_have_tests')"
							:disable="
								event.id !== 0 ||
									disableEvents ||
									!permissions.presential_edit_events
							"
						/>
					</div>

					<div class="col-xs-12 col-sm-6" v-if="event.has_test === 2">
						<q-input
							type="number"
							v-model="event.qty_questions"
							:error="$v.qty_questions.$error"
							@input="$v.qty_questions.$touch"
							:float-label="$t('quantity_questions')"
							:disable="disableEvents || !permissions.presential_edit_events"
						/>
					</div>

					<div class="col-xs-12 col-sm-6" v-if="event.has_test === 2">
						<q-input
							type="number"
							v-model="event.minimum_score_approval"
							:error="$v.minimum_score_approval.$error"
							@input="$v.minimum_score_approval.$touch"
							:float-label="$t('minimum_score')"
							:disable="disableEvents || !permissions.presential_edit_events"
						/>
					</div>

					<!-- WEBCAST DATA - EVENT TYPE === 2 -->
					<div
						class="q-mt-md row full-width"
						v-if="event.event_date_type === 2 && event.id !== 0"
					>
						<q-card
							v-if="event.webcast.id"
							class="action-card q-my-sm q-px-sm q-py-md row"
						>
							<div class="col-12 items-center">
								<q-btn
									class="self-center card-btn"
									color="secondary"
									:label="$t('go_to_webcast')"
									icon="fas fa-broadcast-tower"
									@click="show_webcast_modal = true"
								/>
							</div>
						</q-card>

						<div class="col-xs-12 col-sm-12 q-mb-xs">
							<q-field
								:count="256"
								:helper="$t('leave_it_blank_to_use_the_training_title')"
							>
								<q-input
									v-model="event.webcast.title"
									ref="title"
									:float-label="$t('title')"
								/>
							</q-field>
						</div>

						<div class="col-xs-12 col-sm-12">
							<q-select
								v-if="canSetProvider(event.webcast)"
								v-model="event.webcast.webcast_type"
								:options="getWebcastTypes()"
								:float-label="$t('stream_type')"
								:error="!event.webcast.webcast_type ? true : false"
								chips
								autofocus-filter
								:disable="disableEvents || !permissions.presential_edit_events"
							/>
							<q-select
								v-else
								disable
								readonly
								filled
								hint="Disable and readonly"
								v-model="event.webcast.webcast_type"
								:options="getWebcastTypes()"
								:float-label="$t('stream_type')"
								chips
								autofocus-filter
							/>
						</div>

						<div class="col-xs-12 col-sm-12">
							<q-input
								v-if="
									event.webcast.webcast_type === 1 ||
										event.webcast.webcast_type === 3
								"
								v-model="event.webcast.embed"
								:error="$v.event_dates.$each[index].embed.$error"
								:float-label="$t('transmission_url')"
								:disable="disableEvents || !permissions.presential_edit_events"
							/>
						</div>

						<div
							class="col-xs-12 col-sm-12"
							v-if="selected_client_params.webcast_use_score"
						>
							<q-input
								v-model="event.webcast.score"
								:float-label="$t('score')"
								type="number"
								v-if="
									!selected_client_params.webcast_use_static_score && canSetScore
								"
								:disable="disableEvents || !permissions.presential_edit_events"
							/>

							<q-select
								v-model="event.webcast.score"
								:options="scores"
								:float-label="$t('score')"
								v-if="
									selected_client_params.webcast_use_static_score && canSetScore
								"
								:disable="disableEvents || !permissions.presential_edit_events"
							/>
						</div>

						<div class="q-mt-md col-xs-12 col-sm-6">
							<p class="caption">{{ $t('use_chat') }}</p>
							<q-btn-toggle
								v-model="event.webcast.use_chat"
								:toggle-color="getColorUseChat(event.webcast)"
								:options="chat_options"
								:disable="disableEvents || !permissions.presential_edit_events"
							/>
						</div>

						<div class="q-mt-md col-xs-12 col-sm-6">
							<p class="caption">{{ $t('chat_moderated') }}</p>
							<q-btn-toggle
								v-model="event.webcast.chat_moderated"
								:toggle-color="getColorChatModerated(event.webcast)"
								:options="chat_options"
								:disable="
									disableChatModerated(event.webcast) ||
										disableEvents ||
										!permissions.presential_edit_events
								"
							/>
						</div>

						<div class="row gutter-sm q-mt-sm">
							<div class="col-xs-12 col-sm-12">
								<p class="caption">
									{{ $t('status') }}
									<q-icon
										style="vertical-align: bottom;"
										name="far fa-question-circle"
										size="1.5rem"
										color="grey"
										class="q-ml-sm q-pt-md"
									>
										<q-tooltip
											class="custom-tooltip"
											anchor="top left"
											self="bottom right"
										>
											{{ $t('webcast_status_info') }}
										</q-tooltip>
									</q-icon>
								</p>
								<q-btn-toggle
									v-model="event.webcast.status"
									:toggle-color="getWebcastColorStatus(event.webcast)"
									:options="webcast_statuses"
									:disable="!permissions.presential_edit_events"
								/>
							</div>
						</div>
						<div class="col-xs-12 col-sm-12 q-pa-xs">
							<strong
								><p class="text-right q-pr-lg" v-if="!event.webcast.id">
									<q-icon
										name="fas fa-exclamation-triangle"
										class="text-red q-pr-md q-pb-sm"
										style="font-size: 2rem;"
									/>{{ $t('event_has_not_yet_not_been_saved') }}
								</p></strong
							>
						</div>
					</div>
					<!-- WEBCAST END -->

					<!-- LIVE CLASS - EVENT TYPE === 3 -->
					<div
						class="q-mt-md row full-width"
						v-if="event.event_date_type === 3 && event.id !== 0"
					>
						<q-card
							v-if="event.live_class.id && event.live_class.status === 5"
							class="action-card q-my-sm q-px-sm q-py-md row"
						>
							<div class="col-12 items-center">
								<q-btn
									class="self-center card-btn"
									color="secondary"
									:label="$t('go_to_live_class')"
									icon="fas fa-chalkboard-teacher"
									@click="
										event.live_class.platform_type === 'jitsi'
											? (show_live_class_modal = true)
											: openMeeting(event.live_class.embed)
									"
								/>
							</div>
						</q-card>

						<div class="col-xs-12 col-sm-12 q-mb-xs">
							<q-field
								:count="256"
								:helper="$t('leave_it_blank_to_use_the_training_title')"
							>
								<q-input
									v-model="event.live_class.title"
									ref="title"
									:float-label="$t('title')"
								/>
							</q-field>
						</div>

						<div class="col-xs-12 col-sm-12">
							<q-select
								:helper="$t('leave_it_blank_to_use_the_training_title')"
								v-model="event.live_class.platform_type"
								:options="getLiveClassPlatformTypes"
								:float-label="
									event.live_class.platform_type === 'jitsi'
										? $t('stream_type_jitsi_8x8_warning')
										: $t('stream_type')
								"
								chips
								autofocus-filter
								:disable="!permissions.presential_edit_events"
							/>
						</div>

						<div class="col-xs-12 col-sm-12">
							<q-input
								v-if="event.live_class.platform_type !== 'jitsi'"
								v-model="event.live_class.embed"
								:float-label="$t('transmission_url')"
								:error="$v.event_dates.$each[index].embed.$error"
								@input="$v.event_dates.$each[index].embed.$touch"
								:disable="!permissions.presential_edit_events"
							/>
						</div>

						<div
							class="col-xs-12 col-sm-12"
							v-if="selected_client_params.live_class_use_score"
						>
							<q-input
								v-model="event.live_class.score"
								:float-label="$t('score')"
								type="number"
								v-if="
									!selected_client_params.live_class_use_static_score &&
										canSetScoreLiveClass
								"
								:disable="disableEvents || !permissions.presential_edit_events"
							/>

							<q-select
								v-model="event.live_class.score"
								:options="scores"
								:float-label="$t('score')"
								v-if="
									selected_client_params.live_class_use_static_score &&
										canSetScoreLiveClass
								"
								:disable="disableEvents || !permissions.presential_edit_events"
							/>
						</div>

						<div class="row gutter-sm q-mt-sm">
							<div class="col-xs-12 col-sm-12">
								<p class="caption">
									{{ $t('status') }}
									<q-icon
										style="vertical-align: bottom;"
										name="far fa-question-circle"
										size="1.5rem"
										color="grey"
										class="q-ml-sm q-pt-md"
									>
										<q-tooltip
											class="custom-tooltip"
											anchor="top left"
											self="bottom right"
										>
											{{ $t('live_class_status_info') }}
										</q-tooltip>
									</q-icon>
								</p>
								<q-btn-toggle
									v-model="event.live_class.status"
									:toggle-color="getLiveClassColorStatus(event.live_class)"
									:options="live_class_statuses"
									:disable="!permissions.presential_edit_events"
								/>
							</div>
						</div>
						<div class="col-xs-12 col-sm-12 q-pa-xs">
							<strong
								><p class="text-right q-pr-lg" v-if="!event.live_class.id">
									<q-icon
										name="fas fa-exclamation-triangle"
										class="text-red q-pr-md q-pb-sm"
										style="font-size: 2rem;"
									/>{{ $t('event_has_not_yet_not_been_saved') }}
								</p></strong
							>
						</div>

						<!-- SENCE CODE - CHILE ONLY -->
						<div class="row full-width q-mt-md" v-if="isSence">
							<q-card style="width: 100%;">
								<q-card-title class="bg-blue text-white" style="height: 60px">
									{{ $t('sence_integration') }}
								</q-card-title>
								<q-card-main>
									<div class="row gutter-sm">
										<div class="col-xs-12 col-sm-6">
											<q-field :helper="$t('sence_code_hint')">
												<q-input
													type="text"
													v-model="event.cod_sence"
													:float-label="$t('sence_code')"
													:error="
														$v.event_dates.$each[index].cod_sence.$error
													"
													@input="
														$v.event_dates.$each[index].cod_sence.$touch
													"
												/>
											</q-field>
										</div>
										<div class="col-xs-12 col-sm-6">
											<q-field>
												<q-input
													type="text"
													v-model="event.cod_curso"
													:float-label="$t('sence_course')"
													:error="
														$v.event_dates.$each[index].cod_curso.$error
													"
													@input="
														$v.event_dates.$each[index].cod_curso.$touch
													"
												/>
											</q-field>
										</div>
									</div>
								</q-card-main>
							</q-card>
						</div>
					</div>

					<div
						class="row gutter-sm full-width justify-end q-mt-xs"
						v-if="show_event_buttons"
						:class="$q.platform.is.mobile ? 'q-mx-md' : 'q-mr-md'"
					>
						<div
							class="col-xs-12 col-sm-3"
							:class="getOrder('cancel')"
							v-if="event_tab !== 'new_event'"
						>
							<q-btn
								color="negative"
								class="full-width"
								:label="$t('remove_event')"
								icon="fas fa-trash"
								@click="confirmRemoveEvent(event_dates[event_tab])"
								:disable="disableRemoveEvents"
							/>
						</div>

						<div
							class="col-xs-12 col-sm-3"
							:class="getOrder('save')"
							v-if="showSave && event_tab !== 'new_event'"
						>
							<q-btn
								color="secondary"
								class="full-width"
								:label="$t('save_event')"
								icon="far fa-save"
								@click="updateEvent(event_dates[event_tab])"
							/>
						</div>

						<div
							class="col-xs-12 col-sm-3"
							:class="getOrder('save')"
							v-if="showSave && event_tab === 'new_event'"
						>
							<q-btn
								color="secondary"
								class="full-width"
								:label="$t('save_event')"
								icon="far fa-save"
								@click="saveEvent()"
							/>
						</div>
					</div>
					<!-- LIVE CLASS END -->

					<q-tabs
						class="full-width q-mt-md"
						v-model="test_tab"
						v-if="event.has_test === 3"
					>
						<q-tab slot="title" name="tests" :label="$t('tests')" />

						<q-tab-pane name="tests">
							<tests
								style="padding: 0px;"
								:manage_models="permissions.manage_models"
								:status="status"
								:available_in="event.event_date"
								@toggleEventButtons="toggleEventButtons"
								:id_event="event.id"
								:edit_tests="permissions.edit_tests"
							/>
						</q-tab-pane>
					</q-tabs>
				</div>
			</q-tab-pane>

			<q-tab
				slot="title"
				name="new_event"
				icon="far fa-calendar-plus"
				aria-label="new event"
				v-if="showAddEvents && permissions.presential_edit_events"
			>
				<q-tooltip>
					{{ $t('add_event_tooltip') }}
				</q-tooltip>
			</q-tab>

			<q-tab-pane name="new_event">
				<div class="row gutter-xs">
					<div class="col-12">
						<q-select
							v-model="event_date_type"
							:options="
								event_date_types.map((e) => ({ label: $t(e.slug), value: e.value }))
							"
							:error="$v.event_date_type.$error"
							@input="$v.event_date_type.$touch"
							:float-label="$t('presential_event_type')"
						/>
					</div>

					<div class="col-xs-12 col-sm-6 gutter">
						<q-datetime
							v-model="hr_start"
							:float-label="$t('event_start')"
							type="datetime"
							format="DD/MM/YYYY HH:mm"
							clearable
							:error="$v.hr_start.$error"
							@input="$v.hr_start.$touch"
						/>
					</div>

					<div class="col-xs-12 col-sm-6 gutter">
						<q-datetime
							v-model="hr_end"
							:float-label="$t('event_end')"
							type="datetime"
							format="DD/MM/YYYY HH:mm"
							clearable
							:error="$v.hr_end.$error"
							@input="$v.hr_end.$touch"
						/>
					</div>

					<div class="col-12">
						<q-select
							v-model="has_test"
							:options="event_test_types"
							:error="$v.has_test.$error"
							@input="$v.has_test.$touch"
							:float-label="$t('will_have_tests')"
						/>
					</div>

					<div class="col-xs-12 col-sm-6" v-if="has_test === 2">
						<q-input
							type="number"
							v-model="qty_questions"
							:error="$v.qty_questions.$error"
							@input="$v.qty_questions.$touch"
							:float-label="$t('quantity_questions')"
						/>
					</div>

					<div class="col-xs-12 col-sm-6" v-if="has_test === 2">
						<q-input
							type="number"
							v-model="minimum_score_approval"
							:error="$v.minimum_score_approval.$error"
							@input="$v.minimum_score_approval.$touch"
							:float-label="$t('minimum_score')"
						/>
					</div>
				</div>

				<!-- SENCE CODE - CHILE ONLY -->
				<div class="row q-mt-lg" v-if="isSence && event_date_type === 3">
					<q-card style="width: 100%;">
						<q-card-title class="bg-blue text-white" style="height: 60px">
							{{ $t('sence_integration') }}
						</q-card-title>
						<q-card-main>
							<div class="row gutter-sm">
								<div class="col-xs-12 col-sm-6">
									<q-field :helper="$t('sence_code_hint')">
										<q-input
											type="text"
											v-model="cod_sence"
											:float-label="$t('sence_code')"
											:error="$v.cod_sence.$error"
											@input="$v.cod_sence.$touch"
										/>
									</q-field>
								</div>
								<div class="col-xs-12 col-sm-6">
									<q-field :helper="$t('sence_course_hint')">
										<q-input
											type="text"
											v-model="cod_curso"
											:float-label="$t('sence_course')"
											:error="$v.cod_curso.$error"
											@input="$v.cod_curso.$touch"
										/>
									</q-field>
								</div>
							</div>
						</q-card-main>
					</q-card>
				</div>

				<div
					class="row gutter-sm full-width justify-end q-mt-lg"
					v-if="show_event_buttons"
					:class="$q.platform.is.mobile ? 'q-mx-md' : 'q-mr-md'"
				>
					<div
						class="col-xs-12 col-sm-3"
						:class="getOrder('cancel')"
						v-if="event_tab !== 'new_event'"
					>
						<q-btn
							color="negative"
							class="full-width"
							:label="$t('remove_event')"
							icon="fas fa-trash"
							@click="confirmRemoveEvent(event_dates[event_tab])"
							:disable="disableRemoveEvents"
						/>
					</div>

					<div
						class="col-xs-12 col-sm-3"
						:class="getOrder('save')"
						v-if="showSave && event_tab !== 'new_event'"
					>
						<q-btn
							color="secondary"
							class="full-width"
							:label="$t('save_event')"
							icon="far fa-save"
							@click="updateEvent(event_dates[event_tab])"
						/>
					</div>

					<div
						class="col-xs-12 col-sm-3"
						:class="getOrder('save')"
						v-if="showSave && event_tab === 'new_event'"
					>
						<q-btn
							color="secondary"
							class="full-width"
							:label="event_date_type !== 1 ? $t('create_event') : $t('save_event')"
							:icon="event_date_type !== 1 ? 'far fa-calendar-plus' : 'far fa-save'"
							@click="saveEvent()"
						/>
					</div>
				</div>
			</q-tab-pane>
		</q-tabs>

		<q-dialog v-model="confirm_remove_event" @ok="removeEvent">
			<span slot="title">{{ $t('remove_event') }}</span>

			<span slot="message">{{ $t('confirm_remove_event') }}</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn
					color="primary"
					:label="$t('cancel')"
					@click="confirm_remove_event = false"
				/>
				<q-btn color="negative" :label="$t('remove')" @click="removeEvent()" />
			</template>
		</q-dialog>

		<q-modal
			v-if="show_webcast_modal"
			v-model="show_webcast_modal"
			:content-css="{ minWidth: '100vw', minHeight: '100vh' }"
			no-backdrop-dismiss
			no-esc-dismiss
			maximized
			@hide="hideModal"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="hideModal()" icon="fas fa-times" />
					<q-toolbar-title>
						{{ event_dates[event_tab].webcast.title }}
					</q-toolbar-title>
				</q-toolbar>

				<modal-webcast
					v-if="show_webcast_modal"
					:webcast="event_dates[event_tab].webcast"
					@hideModal="hideModal"
				/>
			</q-modal-layout>
		</q-modal>

		<q-modal
			v-if="show_live_class_modal"
			v-model="show_live_class_modal"
			:content-css="{ minWidth: '100vw', minHeight: '100vh' }"
			no-backdrop-dismiss
			no-esc-dismiss
			maximized
			@hide="hideModal"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="hideModal()" icon="fas fa-times" />
					<q-toolbar-title>
						{{ event_dates[event_tab].live_class.title }}
					</q-toolbar-title>
				</q-toolbar>

				<modal-live-class
					v-if="show_live_class_modal"
					:live_class="event_dates[event_tab].live_class"
					:date_class="event_dates[event_tab].hr_start"
					:id_presential="presential_event_id"
					:id_event="event_dates[event_tab].id"
					@hideModal="hideModal"
				/>
			</q-modal-layout>
		</q-modal>

		<q-dialog
			v-model="show_meet_dialog"
			stack-buttons
			prevent-close
			@cancel="show_meet_dialog = false"
			@hide="show_meet_dialog = false"
		>
			<span slot="title">{{ $t('window_blocked') }}</span>
			<span slot="message">
				{{ $t('browser_window_blocked_click') }}
				<a
					:href="
						event_dates[event_tab] &&
						event_dates[event_tab].live_class &&
						event_dates[event_tab].live_class.embed
							? event_dates[event_tab].live_class.embed
							: null
					"
					target="_blank"
					rel="noopener noreferrer"
					>{{ $t('here') }}</a
				>
				{{ $t('to_access_the_class') }}
			</span>
		</q-dialog>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { date, debounce } from 'quasar'
import { required, requiredIf, between, minValue, maxLength } from 'vuelidate/lib/validators'
const { formatDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'

export default {
	name: 'AdminPresentialEvents',

	props: [
		'status',
		'timezone',
		'events_title',
		'events_description',
		'events_live_class_isPublic',
		'permissions',
		'event_to_finish',
	],

	data() {
		return {
			show_webcast_modal: false,
			show_live_class_modal: false,
			show_event_buttons: true,
			presential_event_id: '',
			event_date_type: 1,
			event_type: 1,
			test_tab: 'tests',
			event_tab: 'new_event',
			has_test: 1,
			event_test_types: [
				{ type: 'no_test', value: 1, label: this.$t('no_test') },
				{ type: 'presential_test', value: 2, label: this.$t('presential_test') },
				{ type: 'online_test', value: 3, label: this.$t('online_test') },
			],
			event_dates: [],
			event_date: '',
			event: [],
			hr_start: null,
			hr_end: null,
			qty_questions: 0,
			minimum_score_approval: null,
			loading: false,
			confirm_remove_event: false,
			selected_event: null,
			title: '',
			description: '',
			chat_options: [
				{ value: true, label: this.$t('yes') },
				{ value: false, label: this.$t('no') },
			],
			webcast: {
				title: '',
				description: this.events_description,
				webcast_type: 1,
				embed: '',
				status: 6,
				score: 0,
				use_chat: true,
				chat_moderated: true,
				transmission_date: null,
				transmission_completed: 0,
				timezone: '',
			},
			webcast_id: null,
			webcast_statuses: [
				{ label: this.$t('published'), value: 6 },
				{ label: this.$t('unpublished'), value: 7 },
				{ label: this.$t('webcast_ended'), value: 8 },
			],
			selected_client_params: [],
			live_class_id: null,
			live_class: {
				title: '',
				description: this.events_description,
				is_public: this.events_live_class_isPublic || false,
				date_class: null,
				qty_limit_participants: 999,
				timezone: '',
				status: 5,
				score: 0,
				code_room: '',
				platform_type: 'jitsi',
				embed: null,
			},
			live_class_statuses: [
				{ label: this.$t('published'), value: 5 },
				{ label: this.$t('unpublished'), value: 6 },
				{ label: this.$t('finished'), value: 7 },
			],
			has_save: false,
			hasSence: false,
			cod_sence: '',
			cod_curso: '',
			linea_capacitacion: 3,
			show_meet_dialog: false,
		}
	},

	components: {
		tests: () => import('components/admin/presential/tests/TestsTab.vue'),
		'modal-webcast': () => import('components/admin/presential/events/ModalWebcast.vue'),
		'modal-live-class': () => import('components/admin/presential/events/ModalLiveClass.vue'),
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			clients: 'params/clients',
			id_user_language_selected: 'common/id_user_language_selected',
			id_user_client_selected: 'common/id_user_client_selected',
			id_user_client_country_selected: 'common/id_user_client_country_selected',
			event_date_types: 'registration_lists/presential_event_date_types',
			live_class_platform_types: 'registration_lists/live_class_platform_types',
			login_oauth2: 'params/login_oauth2',
			scores: 'registration_lists/group_training_scores',
			timezones: 'registration_lists/timezones',
			user_client_module_items: 'common/user_client_module_items',
			user_module_items: 'common/user_module_items',
			webcast_types: 'registration_lists/webcast_types',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		disableEvents() {
			if (
				this.status === 1 ||
				this.status === 2 ||
				this.status === 3 ||
				this.status === 4 ||
				this.status === 6 ||
				this.status === 9 ||
				this.presential_event_id == 0
			)
				return false
			else return true
		},

		disableRemoveEvents() {
			if (this.permissions.presential_edit_events) {
				if (this.status === 9) {
					return this.permissions.manage_models ? false : true
				} else if (
					(this.status === 1 ||
						this.status === 2 ||
						this.status === 6 ||
						this.presential_event_id == 0) &&
					this.event_dates.length > 0
				) {
					return false
				} else return true
			} else return true
		},

		showAddEvents() {
			if (this.status === 1 || this.status === 9) return true
			else return false
		},

		showSave() {
			if (this.permissions.presential_edit_events) {
				if (this.status === 9) {
					if (this.permissions.manage_models) return true
					else return false
				} else if (
					this.status === 1 ||
					this.status === 2 ||
					this.status === 3 ||
					this.status === 4 ||
					this.status === 5 ||
					this.status === 6 ||
					this.presential_event_id == 0
				)
					return true
			} else return false
		},

		canSetScore() {
			const obj = this.user_module_items.find((obj) => obj.slug === 'webcast_set_score')
			return obj ? obj.permission : false
		},

		canSetScoreLiveClass() {
			const obj = this.user_module_items.find((obj) => obj.slug === 'live_class_set_score')
			return obj ? obj.permission : false
		},

		managerTrainings() {
			const obj = this.user_client_module_items.find(
				(obj) => obj.id === this.id_user_client_selected
			)
			if (obj) {
				const item = obj.module_items.find(
					(obj) => obj.slug === 'presential_test_templates'
				)
				if (item) {
					return item.permission
				}
			}
			return true
		},

		getLiveClassPlatformTypes() {
			if (this.live_class_platform_types && this.live_class_platform_types.length) {
				return this.live_class_platform_types.map((type) => {
					return {
						value: type.slug,
						label: type.label ? type.label : '',
					}
				})
			} else return []
		},

		isSence() {
			return (
				process.env.PROJECT_FOLDER === 'universidadechevrolet' &&
				this.id_user_client_country_selected === 48
			)
		},
	},

	methods: {
		requestEvents() {
			this.selected_event = null

			this.loading = true

			this.$axios
				.get(
					`/presential/presential_events/${this.presential_event_id}/event_dates/`,
					this.getHeader
				)
				.then((response) => {
					this.event_dates = response.data.map((e, index) => {
						let returnData = {}

						let cod_sence = ''
						let cod_curso = ''

						if (this.isSence) {
							this.$axios
								.get(
									`/webservices/universidadechevrolet/webservice_sence/presential_event_date/${
										e.id
									}/`,
									this.getHeader
								)
								.then((dataSENCE) => {
									this.event_dates[index].cod_sence =
										dataSENCE.data.code_sence || ''
									this.event_dates[index].cod_curso =
										dataSENCE.data.codigo_curso || ''
								})
								.catch(console.error)
						}

						returnData = {
							id: e.id,
							event_date_type: e.presential_event_date_type,
							event_type: e.event_type,
							event_date: e.event_date,
							hr_end: moment(e.hr_end)
								.tz(e.timezone)
								.format('YYYY-MM-DDTHH:mm:ss'),
							hr_start: moment(e.hr_start)
								.tz(e.timezone)
								.format('YYYY-MM-DDTHH:mm:ss'),
							has_test: e.has_test,
							minimum_score_approval: e.minimum_score_approval,
							qty_questions: e.qty_questions,
							timezone: e.timezone,
							webcast: e.webcast ? e.webcast : this.webcast,
							webcast_id: e.webcast ? e.webcast.id : this.webcast_id,
							live_class: e.live_class.status ? e.live_class : this.live_class,
							live_class_id: e.live_class ? e.live_class.id : this.live_class_id,
							index: index,
							cod_sence: cod_sence,
							cod_curso: cod_curso,
						}

						return returnData
					})
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.loading = false

					if (this.event_to_finish !== null) {
						this.event_tab = String(
							this.event_dates.indexOf(
								this.event_dates.find((e) => e.id === this.event_to_finish)
							)
						)
					} else if (this.show_event_to_finish == null && this.event_dates.length > 0) {
						let highestId = Math.max(...this.event_dates.map((e) => e.id))
						this.event_tab = String(
							this.event_dates.indexOf(
								this.event_dates.find((e) => e.id === highestId)
							)
						)
					}
				})
			this.$v.$reset()
		},

		saveEvent() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				const data = {
					event: {
						presential_event_date_type: this.event_date_type,
						event_type: this.event_type,
						event_date: date.formatDate(this.hr_start, 'YYYY-MM-DD'),
						hr_end: moment(date.formatDate(this.hr_end, 'YYYY-MM-DDTHH:mm'))
							.tz(this.timezone, true)
							.format('YYYY-MM-DDTHH:mm:ssZ'),
						hr_start: moment(date.formatDate(this.hr_start, 'YYYY-MM-DDTHH:mm'))
							.tz(this.timezone, true)
							.format('YYYY-MM-DDTHH:mm:ssZ'),
						has_test: this.has_test,
						minimum_score_approval:
							this.has_test === 2 ? this.minimum_score_approval : null,
						qty_questions: this.has_test === 2 ? this.qty_questions : null,
					},
				}

				this.$axios
					.post(
						`/presential/presential_events/${this.presential_event_id}/event_dates/`,
						data,
						this.getHeader
					)
					.then((response) => {
						if (this.isSence) {
							const sence = {
								code_sence: this.cod_sence ? this.cod_sence : '',
								codigo_curso: this.cod_curso ? this.cod_curso : '',
								linea_capacitacion: this.linea_capacitacion
									? this.linea_capacitacion
									: '',
							}

							this.$axios
								.post(
									`/webservices/universidadechevrolet/webservice_sence/presential_event_date/${
										response.data.id
									}/`,
									sence,
									this.getHeader
								)
								.catch(console.error)
						}

						if (this.event_date_type === 1) {
							this.$q.notify({
								message: this.$t('event_save_success'),
								type: 'positive',
								icon: 'far fa-save',
							})
						} else {
							this.$q.notify({
								message: this.$t('event_created_make_sure_to_save'),
								type: 'warning',
								icon: 'fas fa-exclamation-triangle',
							})
						}

						this.$emit('keepAlive', false)
					})
					.catch((e) => {
						console.log(e)

						this.$q.notify({
							message: this.$t('event_save_error'),
							type: 'negative',
							icon: 'fas fa-ban',
						})
					})
					.finally(() => {
						this.$q.loading.hide()
						this.clearForm()
						this.requestEvents()
					})
			} else {
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fas fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		updateEvent(event) {
			this.has_save = true
			this.selected_event = event
			this.event_date_type = this.selected_event.event_date_type
			this.event_date = this.selected_event.event_date
			this.hr_end = this.selected_event.hr_end
			this.hr_start = this.selected_event.hr_start
			this.has_test = this.selected_event.has_test
			this.minimum_score_approval = this.selected_event.minimum_score_approval
			this.qty_questions = this.selected_event.qty_questions

			if (event.webcast) {
				this.webcast = event.webcast
				this.webcast.title =
					event.webcast.title && event.webcast.title.length
						? event.webcast.title
						: this.events_title
				this.webcast.description = this.events_description
				this.webcast.transmission_date = moment(
					date.formatDate(this.hr_start, 'YYYY-MM-DDTHH:mm')
				)
					.tz(this.timezone, true)
					.format('YYYY-MM-DDTHH:mm:ssZ')
				this.webcast.timezone = event.timezone

				if (event.webcast.id) {
					this.webcast.id = event.webcast.id
				}
				this.webcast.score = event.webcast.score
					? !isNaN(event.webcast.score)
						? event.webcast.score
						: parseInt(event.webcast.score)
					: 0
			}

			if (event.live_class) {
				this.live_class = event.live_class
				this.live_class.title =
					event.live_class.title && event.live_class.title.length
						? event.live_class.title
						: this.events_title
				this.live_class.description = this.events_description
				this.live_class.date_class = moment(
					date.formatDate(this.hr_start, 'YYYY-MM-DDTHH:mm')
				)
					.tz(this.timezone, true)
					.format('YYYY-MM-DDTHH:mm:ssZ')
				this.live_class.timezone = event.timezone
				this.live_class.is_public = this.events_live_class_isPublic
				event.live_class.qty_limit_participants = 999
				event.live_class.platform_type = this.live_class.platform_type
				event.live_class.embed
					? event.live_class.embed.includes('http')
						? event.live_class.embed
						: (event.live_class.embed = `https://${event.live_class.embed}`)
					: (event.live_class.embed = null)
				this.live_class.score = event.live_class.score
					? !isNaN(event.live_class.score)
						? event.live_class.score
						: parseInt(event.live_class.score)
					: 0
				if (event.live_class.id) this.live_class.id = event.live_class.id
			}

			const data = {
				event: {
					presential_event_date_type: this.event_date_type,
					event_type: this.event_type,
					event_date: date.formatDate(this.hr_start, 'YYYY-MM-DD'),
					hr_end: moment(date.formatDate(this.hr_end, 'YYYY-MM-DDTHH:mm'))
						.tz(this.timezone, true)
						.format('YYYY-MM-DDTHH:mm:ssZ'),
					hr_start: moment(date.formatDate(this.hr_start, 'YYYY-MM-DDTHH:mm'))
						.tz(this.timezone, true)
						.format('YYYY-MM-DDTHH:mm:ssZ'),
					has_test: this.has_test,
					minimum_score_approval:
						this.has_test === 2 ? this.minimum_score_approval : null,
					qty_questions: this.has_test === 2 ? this.qty_questions : null,
					webcast: this.event_date_type === 2 ? this.webcast : null,
					live_class: this.event_date_type === 3 ? this.live_class : null,
				},
			}

			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				this.$axios
					.put(
						`/presential/presential_events/${this.presential_event_id}/event_dates/${
							this.selected_event.id
						}/`,
						data,
						this.getHeader
					)
					.then(() => {
						if (this.isSence) {
							const sence = {
								code_sence: event.cod_sence ? event.cod_sence : '',
								codigo_curso: event.cod_curso ? event.cod_curso : '',
								linea_capacitacion: event.cod_sence ? this.linea_capacitacion : '',
							}

							this.$axios
								.post(
									`/webservices/universidadechevrolet/webservice_sence/presential_event_date/${
										this.selected_event.id
									}/`,
									sence,
									this.getHeader
								)
								.catch(console.error)
						}

						this.$q.notify({
							message: this.$t('event_edit_success'),
							type: 'positive',
							icon: 'far fa-save',
						})

						this.$emit('keepAlive', false)
					})
					.catch((e) => {
						console.log(e)

						this.$q.notify({
							message: this.$t('event_edit_error'),
							type: 'negative',
							icon: 'fas fa-ban',
						})
					})
					.finally(() => {
						this.has_save = false
						this.$q.loading.hide()
						this.clearForm()
						this.requestEvents()
					})
			} else {
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fas fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		confirmRemoveEvent(event) {
			this.selected_event = event
			this.confirm_remove_event = true
		},

		removeEvent() {
			this.confirm_remove_event = false
			this.$q.loading.show()

			this.$axios
				.delete(
					`/presential/presential_events/${this.presential_event_id}/event_dates/${
						this.selected_event.id
					}/`,
					this.getHeader
				)
				.then(() => {
					this.$q.notify({
						message: this.$t('event_remove_success'),
						type: 'positive',
						icon: 'fas fa-trash',
					})
					this.$emit('keepAlive', false)
				})
				.catch((e) => {
					console.log(e)

					this.$q.notify({
						message: this.$t('event_remove_error'),
						type: 'negative',
						icon: 'fas fa-ban',
					})
				})
				.finally(() => {
					this.clearForm()
					this.requestEvents()
					this.$q.loading.hide()
				})
		},

		clearForm() {
			this.$v.$reset()
			this.event_date_type = 1
			this.event_date = null
			this.hr_end = null
			this.hr_start = null
			this.has_test = 1
			;(this.webcast_id = null), (this.live_class_id = null)
			this.webcast = {
				chat_moderated: true,
				embed: '',
				score: 0,
				status: 6,
				timezone: '',
				transmission_completed: 0,
				transmission_date: null,
				use_chat: true,
				webcast_type: 1,
			}
			this.live_class = {
				code_room: '',
				date_class: null,
				embed: null,
				is_public: this.events_live_class_isPublic,
				platform_type: 'jitsi',
				qty_limit_participants: 999,
				score: 0,
				status: 5,
				timezone: '',
			}
		},

		getOrder(name) {
			if (name == 'cancel') {
				return this.$q.platform.is.mobile ? 'order-last' : 'order-first'
			} else if (name == 'save') {
				return this.$q.platform.is.mobile ? 'order-first' : 'order-last'
			}
		},

		fDate(d) {
			if (d) {
				let format = ''

				switch (this.id_user_language_selected) {
					case 1:
					case 3:
						format = 'DD/MM/YYYY HH:mm'
						break
					default:
						format = 'MM-DD-YYYY HH:mm'
						break
				}

				return date.formatDate(d, format)
			}
		},

		toggleEventButtons(value) {
			this.show_event_buttons = value
		},

		// WEBCAST RELATED:
		getWebcastTypes() {
			if (this.webcast_types) {
				return this.webcast_types.map((type) => {
					return {
						value: type.value,
						label: type.label,
					}
				})
			}
		},

		canSetProvider(event) {
			return event.webcast_type === 2 ? false : true
		},

		getColorUseChat(webcast) {
			return webcast.use_chat === true ? 'positive' : 'negative'
		},

		getColorChatModerated(webcast) {
			return webcast.chat_moderated === true ? 'positive' : 'negative'
		},

		disableChatModerated(webcast) {
			if (!webcast.use_chat) {
				webcast.chat_moderated = false
				return true
			} else return false
		},

		getWebcastColorStatus(webcast) {
			if (webcast.status == 6) return 'positive'
			else if (webcast.status == 7) return 'warning'
			else if (webcast.status == 8) return 'negative'
			else return 'negative'
		},

		// LIVE CLASS RELATED
		getLiveClassColorStatus(liveclass) {
			if (liveclass.status == 5) return 'positive'
			else if (liveclass.status == 6) return 'warning'
			else if (liveclass.status == 7) return 'negative'
			else return 'negative'
		},

		hideModal() {
			this.show_webcast_modal = false
			this.show_live_class_modal = false
		},

		openMeeting(url) {
			const meet = window.open(url, '_blank')
			if (meet === null) {
				this.show_meet_dialog = true
			} else return
		},
	},

	created() {
		this.presential_event_id = this.$route.params.id
		this.selected_client_params = this.clients.find((obj) => {
			return obj.id === this.id_user_client_selected
		})
		this.requestEvents()
	},

	validations() {
		return {
			event_date_type: { required },
			hr_start: { required },
			hr_end: {
				required,
				minValue: (value, event_date) => {
					return event_date.hr_start ? value > event_date.hr_start : true
				},
			},
			has_test: { required },
			qty_questions: {
				requiredIf: requiredIf((event) => {
					return event.has_test === 2 ? true : false
				}),
			},
			minimum_score_approval: {
				requiredIf: requiredIf((event) => {
					return event.has_test === 2 ? true : false
				}),
			},

			event_dates: {
				$each: {
					embed: {
						requiredIf: requiredIf((e) => {
							if (this.has_save) {
								if (e.event_date_type == 2 && this.event_tab == e.index) {
									if (e.webcast.embed) return false
									else return true
								} else if (e.event_date_type == 3 && this.event_tab == e.index) {
									if (e.live_class.platform_type === 'jitsi') {
										return false
									} else {
										if (e.live_class.embed) return false
										else return true
									}
								}
							} else return false
						}),
					},
					cod_sence: {
						requiredIf: requiredIf((e) => {
							if (
								e.event_date_type == 3 &&
								this.event_tab == e.index &&
								this.isSence
							) {
								if (e.cod_curso !== '') return true
								else return false
							}
						}),
					},
					cod_curso: {
						requiredIf: requiredIf((e) => {
							if (
								e.event_date_type == 3 &&
								this.event_tab == e.index &&
								this.isSence
							) {
								if (e.cod_sence !== '') return true
								else return false
							}
						}),
					},
				},
			},
			cod_sence: {
				requiredIf: requiredIf(() => {
					return this.cod_curso === '' ? false : true
				}),
			},
			cod_curso: {
				requiredIf: requiredIf(() => {
					return this.cod_sence === '' ? false : true
				}),
			},
		}
	},
}
</script>

<style lang="stylus" scoped>
.event-card
    box-shadow 0 1px 5px rgba(0,0,0,0.2), 0 2px 2px rgba(0,0,0,0.14), 0 3px 1px -2px rgba(0,0,0,0.12)

.action-card
    width: 100%

.card-btn
    display block
    min-width 30%
    margin 0 auto

.custom-tooltip
    width 450px
    font-size 0.9rem
    padding 0.8rem
</style>
